import {
    GelRowLayout,
    GelSpinner,
    GelParagraph,
    GelLink,
    GelCollapsible,
    GelContainerLite,
    GelBoxLayout,
    GelLabel,
    GelIcon
} from '@tal-gel/components';
import { getGelTokens } from '@tal-gel/theming';
import { useCallback, useContext, useState } from 'react';
import { useCookies } from 'react-cookie';
import { UserContext } from '../../../common/usercontext/user.context';
import { AdobeClickType } from '../../../constants/constants';
import { MARK_HISTORICAL_PAYMENTS_AS_READ } from '../../../graphql/queries/graphql-mutations';
import { AdobeAnalytics } from '../../../common/analytics/adobe-analytics';
import { Requirements } from '../../../common/models/memberclaims';
import { useGetClaimDetailsContent } from '../../../common/api/graphQLDataFetch';
import { useNavigate } from 'react-router-dom';
import MmcGraphQL from '../../../common/api/mmc-graphql';
import { activeUploadActions, useActiveUploadStore } from '../documentsupload/activeUploadStore';

interface ProceesedPaymentProps {
    payment: Requirements;
}
const ProcessedPayment = ({ payment }: ProceesedPaymentProps) => {
    const { global: { themeColorBorderDefault, sizeBaseUnit, themeColorIconDefault } } = getGelTokens();

    const [{ aid, rid, ssoid, tpid }] = useCookies(['aid', 'rid', 'ssoid', 'tpid']);
    const { contextData, setUserContext } = useContext(UserContext);
    const [notificationUpdating, setNotificationUpdating] = useState(false);
    const navigate = useNavigate();
    
    const { data } = useGetClaimDetailsContent()

    const activeId = useActiveUploadStore(s => s.activeRequirementId);
    const isOpen = activeId === payment.id;

    const onHeaderClick = useCallback(() => {
        if (isOpen) {
            setNotificationUpdating(true);
            markPaymentAsRead()
                .finally(() => setNotificationUpdating(false));
        }

        const opening = !isOpen;
        activeUploadActions.setActiveRequirement(opening ? (payment.id ?? null) : null);

        activeUploadActions.consumeUploadComplete();

        AdobeAnalytics.PushClickEvent(tpid, data?.nextPayment?.PaymentMadeHeader!, AdobeClickType.Button)

    }, [isOpen]);

    const onViewDetailsClick = () => {
        navigate(`/payment/${payment.id}`);
        MmcGraphQL<{ paymentIds: number[] }>(MARK_HISTORICAL_PAYMENTS_AS_READ, { paymentIds: [payment.id] })
            .then(markPaymentAsRead);
    }

    const markPaymentAsRead = () =>
        MmcGraphQL<{ paymentIds: number[] }>(MARK_HISTORICAL_PAYMENTS_AS_READ, { paymentIds: [payment.id] })
            .then(() => {
                setUserContext((prev: UserContext) => {
                    if (!prev) return prev;

                    const claims = (prev.claims ?? []).map(c => ({
                        ...c,
                        requirements: (c.requirements ?? []).filter(req => req.id !== payment.id),
                    }));

                    const historicalPayments = (prev.historicalPayments ?? []).map(hp => ({
                        ...hp,
                        isRead: hp.paymentId === payment.id ? true : hp.isRead,
                    }));

                    return { ...prev, claims, historicalPayments };
                });
            })

    return (
        <div id="processedPayment">
            <GelContainerLite style={{
                borderTop: `${sizeBaseUnit / 4}px solid ${themeColorBorderDefault}`,
                borderLeft: `${sizeBaseUnit / 4}px solid ${themeColorBorderDefault}`,
                borderRight: `${sizeBaseUnit / 4}px solid ${themeColorBorderDefault}`,
                paddingTop: sizeBaseUnit,
                paddingBottom: sizeBaseUnit,
                width: "auto",
                cursor: "pointer"
            }}
                onClick={onHeaderClick}>

                <GelBoxLayout space={[7, 0.1, 0.04]} style={{
                    borderBottom: isOpen ? `${sizeBaseUnit / 4}px solid ${themeColorBorderDefault}` : "",
                    width: "auto",
                    padding: sizeBaseUnit * 2,
                    marginLeft: -sizeBaseUnit * 4,
                    marginRight: -sizeBaseUnit * 4,
                }}>
                    <GelLabel large>
                        {data?.nextPayment?.PaymentMadeHeader}
                    </GelLabel>

                    <GelIcon name={isOpen ? "ChevronUp" : "ChevronDown"} color={themeColorIconDefault} width="20px" height="20px" />
                </GelBoxLayout>
            </GelContainerLite>

            <GelContainerLite style={{
                borderBottom: `${sizeBaseUnit / 4}px solid ${themeColorBorderDefault}`,
                borderLeft: `${sizeBaseUnit / 4}px solid ${themeColorBorderDefault}`,
                borderRight: `${sizeBaseUnit / 4}px solid ${themeColorBorderDefault}`,
                width: "auto"
            }}>

                <GelCollapsible open={isOpen || notificationUpdating}>
                    <GelContainerLite style={{ padding: sizeBaseUnit * 4 }}>
                        <GelRowLayout>
                            <GelParagraph>
                                {payment?.description}
                            </GelParagraph>
                        </GelRowLayout>
                        <GelRowLayout style={{ paddingTop: sizeBaseUnit * 6 }} >
                            {notificationUpdating && <GelSpinner medium overlay />}
                            <GelLink onClick={onViewDetailsClick}>View payment details</GelLink>
                        </GelRowLayout>
                    </GelContainerLite>
                </GelCollapsible>

            </GelContainerLite>
        </div>
    )
}

export default ProcessedPayment;